import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TransferLinkParameters } from '@app/client/uploader/interfaces/transfer';
import { PrepareActions } from '@app/client/uploader/store/actions';
import { Domain } from '@app/shared/models/domain';
import { TransferSettings } from '@app/shared/models/domainSettings';
import { Identity } from '@app/shared/models/identity';
import { Transfer } from '@app/shared/models/transfer';
import { LinkDelivery } from '@app/shared/models/transferConfiguration';
import { Store } from '@ngrx/store';
import { UploaderStatus } from '@smash-sdk/uploader';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as fromUploader from '../../../../store/reducers';

@Component({
  selector: 'app-link-form',
  templateUrl: './link-form.component.html',
  styleUrls: ['./link-form.component.scss']
})
export class LinkFormComponent implements OnInit {

  @Input() identity: Identity;
  @Input() domain: Domain;
  @Input() domainTransferSettings: TransferSettings;

  @Output() linkFormSubmitted: EventEmitter<{
    title: string,
    customUrl: string,
    delivery: LinkDelivery,
  }> = new EventEmitter();

  uploadError$: Observable<any>;
  transfer$: Observable<Transfer>;
  linkParameters$: Observable<TransferLinkParameters>;
  uploadStatus$: Observable<UploaderStatus>;

  linkForm: FormGroup;
  submitted = false;
  prepareState: any;
  errorMessageLinkAlreadyExists = Constant.errorStatus.uploader.linkAlreadyExists;
  showMessageDisabled = false;

  constructor(
    private store$: Store<fromUploader.State>,
    private readonly router: Router
  ) {
    this.uploadError$ = this.store$.select(fromUploader.getUploadError);
    this.transfer$ = this.store$.select(fromUploader.getTransfer);
    this.linkParameters$ = this.store$.select(fromUploader.getTransferLinkParameters);
    this.uploadStatus$ = this.store$.select(fromUploader.getUploadStatus);

    this.linkForm = new FormGroup({
      title: new FormControl(''),
      sender: new FormGroup({
        email: new FormControl('', [Validators.required, Validators.pattern(Constant.regexpEmail)]),
      }),
      customUrl: new FormControl('', Validators.pattern(Constant.regexpCustomUrl)),
      accessTracking: new FormControl(this.domainTransferSettings?.accessTracking?.default === Constant.accessTracking.Email ? true : false),
    });
  }

  ngOnInit() {
    this.loadFormData();
    this.handleFormChanges();
  }

  get f() { return this.linkForm.controls; }
  get senderEmailControls() { return this.linkForm.get('sender.email'); }

  onSubmit() {
    this.submitted = true;
    if (this.linkForm.valid) {
      const { title, sender, customUrl } = this.linkForm.value;
      const buildedLinkFormValue = {
        title,
        customUrl,
        delivery: {
          type: 'Link' as const,
          sender,
        },
      };
      this.linkFormSubmitted.emit(buildedLinkFormValue);
    }
  }

  goBack() {
    this.router.navigateByUrl('');
  }

  onTitleChange(value) {
    // tslint:disable-next-line: no-string-literal
    this.linkForm.controls['customUrl'].setValue(this._slugify(value));
  }

  _slugify(str: string) {
    const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;';
    const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------';
    const p = new RegExp(a.split('').join('|'), 'g');

    return str ? str.toString() // .toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(p, c => b.charAt(a.indexOf(c))) // Replace special characters
      .replace(/&/g, '-and-') // Replace & with 'and'
      .replace(/[^\w\-]+/g, '') // Remove all non-word characters
      .replace(/\-\-+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, '')  // Trim - from end of text
      : null;
  }

  inputClick(event) {
    if (event.target.offsetParent.className.indexOf('disabled') > -1) {
      this.showMessageDisabled = true;
    }
    event.preventDefault();
    event.stopPropagation();
  }

  loadFormData() {
    this.linkParameters$.pipe(first()).subscribe((params) => {
      let accessTracking = this.domainTransferSettings?.accessTracking?.default === Constant.accessTracking.Email ? true : false;
      if (params.accessTracking) {
        accessTracking = params.accessTracking === Constant.accessTracking.Email ? true : false;
      }

      this.linkForm.patchValue({ ...params, ...params.delivery, accessTracking });
    });
  }

  handleFormChanges() {
    this.linkForm.valueChanges.subscribe(changes => {
      this.store$.dispatch(PrepareActions.SetTransferSender({ sender: changes.sender }));
      this.store$.dispatch(PrepareActions.SetLinkTitle({ title: changes.title }));
      this.store$.dispatch(PrepareActions.SetLinkCustomUrl({ customUrl: changes.customUrl }));
      this.store$.dispatch(PrepareActions.SetTransferAccessTracking({ accessTracking: changes.accessTracking ? 'Email' : 'None' }));
    });
  }

  get isAccessTrackingEnabled(): boolean {
    return this.domainTransferSettings?.accessTracking?.active;
  }

  get customUrlAlreadyInUseErrorName(): string {
    return Constant.uploaderErrorType.customUrlAlreadyInUse;
  }
}
